/**
 * 404 Not Found 에러 페이지 컴포넌트
 *
 * 주요 기능:
 * - 존재하지 않는 라우트 접근 시 표시되는 에러 페이지
 * - 뒤로 가기 및 홈으로 이동 버튼 제공
 * - 중앙 정렬된 에러 메시지와 안내 표시
 *
 * 사용처:
 * - router/index.tsx의 catch-all 라우트 (*경로)
 * - 잘못된 URL 입력 시 자동 표시
 */

import { useNavigate } from "react-router-dom";
import styled from "styled-components";

/**
 * Not Found Page 컴포넌트
 *
 * 404 에러 페이지를 표시하고 사용자 네비게이션 옵션 제공
 *
 * @returns 404 에러 페이지 레이아웃
 */
const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container>
      <Content>
        <ErrorCode>404</ErrorCode>
        <Title>Page Not Found</Title>
        <Description>
          The page you&apos;re looking for doesn&apos;t exist.
        </Description>

        <ButtonGroup>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
          <PrimaryButton onClick={() => navigate("/")}>Go Home</PrimaryButton>
        </ButtonGroup>
      </Content>
    </Container>
  );
};

export default NotFoundPage;

// Styled Components
const Container = styled.div`
  min-height: calc(100vh - 380px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
`;

const Content = styled.div`
  text-align: center;
  max-width: 500px;
`;

const ErrorCode = styled.h1`
  font-size: 96px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 16px 0 8px;
  color: ${({ theme }) => theme.colors.fontBlack};
`;

const Description = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.fontGray[350]};
  margin: 0 0 32px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.borderGray};
  color: ${({ theme }) => theme.colors.fontBlack};

  &:hover {
    background: ${({ theme }) => theme.colors.bgGray[700]};
    color: ${({ theme }) => theme.colors.fontBlack};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(90deg, #6b8eff 0%, #9fe2ff 100%);
  color: white;
  border: none;
`;
